/**
 * pageOverrides.js
 *
 * Stores user-confirmed URL → page-code mappings in localStorage so that
 * the IU taxonomy validator can correctly identify which page type a GAM
 * request belongs to, even when auto-detection from the URL is uncertain.
 *
 * Storage key: "adlens_page_overrides"
 * Shape: { [urlKey: string]: { pageCode: string, savedAt: number, label: string } }
 *
 * urlKey is derived from the content_url: "hostname + pathname prefix"
 * e.g. "www.foxnews.com/shows" → "hp"
 */

const STORAGE_KEY = 'adlens_page_overrides';

/** Read all saved overrides from localStorage */
export function getPageOverrides() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Persist the full overrides map */
function setPageOverrides(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

/**
 * Save a single URL → page code mapping.
 * @param {string} contentUrl - full URL of the page being tested
 * @param {string} pageCode   - confirmed page code e.g. "hp", "art", "sf"
 * @param {string} [label]    - human readable label e.g. "The App Homepage"
 */
export function savePageOverride(contentUrl, pageCode, label = '') {
  const key = urlToKey(contentUrl);
  if (!key) return;
  const overrides = getPageOverrides();
  overrides[key] = { pageCode, savedAt: Date.now(), label: label || key };
  setPageOverrides(overrides);
}

/** Remove a single override */
export function removePageOverride(contentUrl) {
  const key = urlToKey(contentUrl);
  if (!key) return;
  const overrides = getPageOverrides();
  delete overrides[key];
  setPageOverrides(overrides);
}

/** Clear all saved overrides */
export function clearPageOverrides() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/**
 * Look up a saved page override for a given content URL.
 * Tries progressively shorter path prefixes until a match is found.
 * Returns null if no override exists.
 *
 * @param {string} contentUrl
 * @returns {{ pageCode: string, key: string, label: string } | null}
 */
export function lookupPageOverride(contentUrl) {
  const overrides = getPageOverrides();
  if (!contentUrl || Object.keys(overrides).length === 0) return null;

  try {
    const u = new URL(contentUrl);
    const host = u.hostname;
    const segments = u.pathname.split('/').filter(Boolean);

    // Try full path down to just the hostname (longest match wins)
    for (let len = segments.length; len >= 0; len--) {
      const key = len === 0
        ? host
        : `${host}/${segments.slice(0, len).join('/')}`;
      if (overrides[key]) {
        return { key, ...overrides[key] };
      }
    }
  } catch {}

  return null;
}

/**
 * Convert a full URL to a stable storage key.
 * Uses hostname + first two path segments (enough to disambiguate page types).
 */
export function urlToKey(contentUrl) {
  try {
    const u = new URL(contentUrl);
    const host = u.hostname;
    const segments = u.pathname.split('/').filter(Boolean);
    // Use up to 2 path segments for granularity
    const prefix = segments.slice(0, 2).join('/');
    return prefix ? `${host}/${prefix}` : host;
  } catch {
    return '';
  }
}

/**
 * Return a human-readable description of what a saved key covers.
 * e.g. "www.foxnews.com/shows" → "foxnews.com/shows/*"
 */
export function keyToLabel(key = '') {
  return key.replace(/^www\./, '') + '/*';
}
